import { createContext, useContext, useMemo, useState } from 'react';

import cx from 'classnames';

import { useUserContext } from '~/contexts/UserContext';
import useScrollPosition from '~/hooks/useScrollPosition';

import styles from './stickyScrollContext.module.less';

type StickyScrollContextValues = {
  direction: '' | 'up' | 'down';
  innerZ: number;
  top: number;
  isGuest: boolean;
  isStickyActive: boolean;
  setIsStickyActive: (isStickyActive: boolean) => void;
};

const StickyScrollContext = createContext<StickyScrollContextValues | undefined>(undefined);

export const useStickyScrollContext = () => {
  const context = useContext(StickyScrollContext);
  if (context === undefined) {
    throw new Error('useStickyScrollContext must be used within a StickyScrollProvider');
  }
  return context;
};

export const StickyScrollProvider: React.FC = ({ children }) => {
  const { direction } = useScrollPosition();
  const [isStickyActive, setIsStickyActive] = useState(false);
  const userContext = useUserContext();

  const isGuest = useMemo(
    () => (userContext ? Boolean(userContext.is_guest) : true),
    [userContext]
  );

  const innerZ = useMemo(() => (isGuest ? 500 : 19), [isGuest]);
  const top = useMemo(() => (isGuest ? 0 : 50), [isGuest]);

  return (
    <StickyScrollContext.Provider
      value={{
        direction,
        innerZ,
        top,
        isGuest,
        isStickyActive,
        setIsStickyActive,
      }}
    >
      <div
        className={cx({
          [styles.mainLayoutStickyActive]: isGuest && !isStickyActive && direction === 'down',
        })}
      >
        {children}
      </div>
    </StickyScrollContext.Provider>
  );
};
